import React, { useMemo, useState } from 'react';
import { Search, ChevronDown, Filter, X, ShoppingCart, Eye } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useScreenInit } from '../useScreenInit.js';
import { ProductCard } from '../components/ProductCard';
import { SocialShopping } from '../components/SocialShopping';
const categories = ['All Products', 'New Arrivals', 'TriLyn Collection', 'Victoria By TriLyn', 'Zarrito By TriLyn', 'Accessories', 'Clearance', 'Gift Cards'];
const filterGroups = [{
  name: 'Collection',
  key: 'collection',
  options: ['TriLyn', 'Victoria', 'Zarrito', 'Heritage']
}, {
  name: 'Product Type',
  key: 'type',
  options: ['Clothing', 'Jewelry', 'Accessories', 'Gift Cards']
}, {
  name: 'Availability',
  key: 'availability',
  options: ['In Stock', 'Pre-order']
}];
const sortOptions = ['Featured', 'Newest', 'Best Sellers', 'Price Low to High', 'Price High to Low', 'Sale Items'];
// Mock data generator for functional demo
const allProducts = Array.from({
  length: 24
}).map((_, i) => ({
  id: i + 1,
  name: i % 5 === 0 ? `Victoria Gold Piece ${i}` : i % 3 === 0 ? `Zarrito Kaftan ${i}` : `Heritage Statement Piece ${i}`,
  price: (100 + i * 15).toFixed(2),
  numericPrice: 100 + i * 15,
  salePrice: i % 4 === 0 ? (80 + i * 10).toFixed(2) : undefined,
  numericSalePrice: i % 4 === 0 ? 80 + i * 10 : undefined,
  image: ['https://images.unsplash.com/photo-1548036328-c9fa89d128fa', 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce', 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b', 'https://images.unsplash.com/photo-1589156280159-27698a70f29e', 'https://images.unsplash.com/photo-1535632066927-ab7c9ab60908', 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d'][i % 6] + '?q=80&w=1000&auto=format&fit=crop',
  collection: i % 3 === 0 ? 'Victoria' : i % 2 === 0 ? 'TriLyn' : 'Zarrito',
  type: i % 4 === 0 ? 'Jewelry' : 'Clothing',
  availability: i % 10 === 0 ? 'Pre-order' : 'In Stock',
  rating: 4 + i % 2,
  date: new Date(2024, 0, i + 1).getTime(),
  isBestSeller: i % 7 === 0
}));
// Add the dedicated eGift Card product to the list
const giftCardProduct = {
  id: 'gift-card',
  name: 'TriLyn eGift Card',
  price: '25.00',
  numericPrice: 25,
  image: 'https://cdn.magicpatterns.com/uploads/43ocbeErzL86xDTvHUMoKt/56bc39_3b36d8682b4e40f4bc9e19e8307687eb~mv2.jpg',
  collection: 'Gift Cards',
  type: 'Gift Cards',
  availability: 'In Stock',
  rating: 5,
  date: Date.now(),
  isBestSeller: true
};
const newProducts = [{
  id: 25,
  name: 'Fila Cap - Gold',
  price: '60.00',
  numericPrice: 60,
  image: 'https://cdn.magicpatterns.com/uploads/1NAtNJ2fcku1izttueGKgP/pasted-image.jpg',
  collection: 'TriLyn',
  type: 'Accessories',
  availability: 'In Stock',
  rating: 5,
  date: Date.now()
}, {
  id: 26,
  name: 'Trilyn Bag Set',
  price: '100.00',
  numericPrice: 100,
  image: 'https://cdn.magicpatterns.com/uploads/iMXocAq8RByxdFHyhLJkKz/pasted-image.jpg',
  collection: 'TriLyn',
  type: 'Accessories',
  availability: 'In Stock',
  rating: 5,
  date: Date.now()
}];
const finalProducts = [...newProducts, ...allProducts, giftCardProduct];
export function Shop() {
  useScreenInit();
  const [activeCategory, setActiveCategory] = useState('All Products');
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [sortBy, setSortBy] = useState('Newest');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({
    collection: [],
    type: [],
    availability: []
  });
  const toggleFilter = (key: string, option: string) => {
    setSelectedFilters(prev => ({
      ...prev,
      [key]: prev[key].includes(option) ? prev[key].filter(o => o !== option) : [...prev[key], option]
    }));
    setCurrentPage(1);
  };
  const filteredProducts = useMemo(() => {
    let result = [...finalProducts];
    // Category Filter
    if (activeCategory !== 'All Products') {
      if (activeCategory === 'Gift Cards') result = result.filter(p => p.type === 'Gift Cards');else if (activeCategory === 'Accessories') result = result.filter(p => p.type === 'Accessories' || p.type === 'Jewelry');else if (activeCategory === 'Clearance') result = result.filter(p => p.salePrice);else if (activeCategory.includes('Victoria')) result = result.filter(p => p.collection === 'Victoria');else if (activeCategory.includes('Zarrito')) result = result.filter(p => p.collection === 'Zarrito');else if (activeCategory.includes('TriLyn')) result = result.filter(p => p.collection === 'TriLyn');
    }
    // Search Filter
    if (searchQuery) {
      result = result.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    // Sidebar Filters
    Object.entries(selectedFilters).forEach(([key, values]) => {
      if (values.length > 0) {
        result = result.filter(p => values.includes(p[key as keyof typeof p] as string));
      }
    });
    // Sorting
    if (sortBy === 'Price Low to High') result.sort((a, b) => (a.numericSalePrice || a.numericPrice) - (b.numericSalePrice || b.numericPrice));
    if (sortBy === 'Price High to Low') result.sort((a, b) => (b.numericSalePrice || b.numericPrice) - (a.numericSalePrice || a.numericPrice));
    if (sortBy === 'Newest') result.sort((a, b) => b.date - a.date);
    if (sortBy === 'Best Sellers') result.sort((a, b) => (b.isBestSeller ? 1 : 0) - (a.isBestSeller ? 1 : 0));
    if (sortBy === 'Sale Items') result = result.filter(p => p.salePrice);
    return result;
  }, [activeCategory, searchQuery, selectedFilters, sortBy]);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({
      top: 400,
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-white" data-id="element-928">
      {/* Shop Hero */}
      <section className="relative py-20 bg-black text-white overflow-hidden" data-id="element-929">
        <div className="absolute inset-0 opacity-40" data-id="element-930">
          <img src="https://cdn.magicpatterns.com/uploads/fvsZFg6psQfPV8BjBoKS4M/pasted-image.png" className="w-full h-full object-cover" alt="Shop Background" data-id="element-931" />
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center" data-id="element-932">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-933">
            <h1 className="text-5xl md:text-7xl font-serif mb-6 tracking-tight leading-tight" data-id="element-934">
              Shop TriLyn
            </h1>
            <p className="text-base md:text-lg text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed font-light" data-id="element-935">
              Explore clothing, accessories, statement pieces, clearance items,
              and gift cards inspired by African culture and modern elegance.
            </p>
            <button onClick={() => {
            setActiveCategory('New Arrivals');
            setCurrentPage(1);
          }} className="bg-gold text-black px-10 py-4 text-sm uppercase tracking-widest font-bold hover:bg-white transition-all duration-300" data-id="element-936">
              Shop New Arrivals
            </button>
          </motion.div>
        </div>
      </section>

      {/* Category Navigation */}
      <nav className="border-b border-gray-100 sticky top-0 bg-white z-40 overflow-x-auto no-scrollbar" data-id="element-937">
        <div className="container mx-auto px-4" data-id="element-938">
          <motion.ul initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="flex whitespace-nowrap space-x-8 py-6 text-xs uppercase tracking-[0.2em] font-bold" data-id="element-939">
            {categories.map(cat => <li key={cat} data-id="element-940">
                <button onClick={() => {
              setActiveCategory(cat);
              setCurrentPage(1);
            }} className={`transition-colors hover:text-gold ${activeCategory === cat ? 'text-gold border-b-2 border-gold pb-1' : 'text-gray-400'}`} data-id="element-941">
                  {cat}
                </button>
              </li>)}
          </motion.ul>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8" data-id="element-942">
        <div className="flex flex-col lg:flex-row gap-12 items-start" data-id="element-943">
          {/* Desktop Filters Sidebar */}
          <motion.aside initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6
        }} viewport={{
          once: true
        }} className="hidden lg:block w-64 flex-shrink-0 space-y-10 sticky top-24 h-fit" data-id="element-944">
            {/* Search */}
            <div data-id="element-945">
              <h3 className="text-xs uppercase tracking-widest font-bold mb-4" data-id="element-946">
                Search
              </h3>
              <div className="relative" data-id="element-947">
                <input type="text" placeholder="Search products" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full border-b border-gray-300 py-2 pl-8 focus:outline-none focus:border-gold transition-colors text-sm" data-id="element-948" />
                <Search className="absolute left-0 top-2.5 text-gray-400" size={18} data-id="element-949" />
              </div>
            </div>

            {/* Filter Groups */}
            {filterGroups.map(group => <div key={group.name} className="border-t border-gray-100 pt-6" data-id="element-950">
                <h3 className="text-xs uppercase tracking-widest font-bold mb-4 flex justify-between items-center" data-id="element-951">
                  {group.name}
                  <ChevronDown size={14} data-id="element-952" />
                </h3>
                <ul className="space-y-3" data-id="element-953">
                  {group.options.map(opt => <li key={opt} onClick={() => toggleFilter(group.key, opt)} className="flex items-center gap-3 group cursor-pointer" data-id="element-954">
                      <div className={`w-4 h-4 border transition-colors rounded-sm ${selectedFilters[group.key].includes(opt) ? 'bg-gold border-gold' : 'border-gray-300 group-hover:border-gold'}`} data-id="element-955" />
                      <span className={`text-sm transition-colors ${selectedFilters[group.key].includes(opt) ? 'text-black font-bold' : 'text-gray-600 group-hover:text-black'}`} data-id="element-956">
                        {opt}
                      </span>
                    </li>)}
                </ul>
              </div>)}

            <button onClick={() => setSelectedFilters({
            collection: [],
            type: [],
            availability: []
          })} className="text-[10px] uppercase tracking-widest font-bold text-gray-400 hover:text-gold transition-colors" data-id="element-957">
              Clear All Filters
            </button>
          </motion.aside>

          {/* Product Grid */}
          <main className="flex-1" data-id="element-958">
            <div className="flex justify-between items-center mb-8" data-id="element-959">
              <div className="flex items-center gap-4" data-id="element-960">
                <button onClick={() => setShowMobileFilters(true)} className="lg:hidden flex items-center gap-2 bg-black text-white px-4 py-2 text-[10px] uppercase tracking-widest font-bold" data-id="element-961">
                  <Filter size={14} data-id="element-962" /> Filters
                </button>
                <p className="text-[10px] md:text-sm text-gray-500 uppercase tracking-widest" data-id="element-963">
                  {filteredProducts.length} Products
                </p>
              </div>
              <div className="hidden lg:flex items-center gap-4 text-xs uppercase tracking-widest font-bold" data-id="element-964">
                <span className="text-gray-400" data-id="element-965">Sort By:</span>
                <div className="relative group/sort" data-id="element-966">
                  <button className="flex items-center gap-2" data-id="element-967">
                    {sortBy} <ChevronDown size={14} data-id="element-968" />
                  </button>
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-100 shadow-xl opacity-0 invisible group-hover/sort:opacity-100 group-hover/sort:visible transition-all z-50" data-id="element-969">
                    {sortOptions.map(opt => <button key={opt} onClick={() => setSortBy(opt)} className={`w-full text-left px-4 py-3 text-[10px] uppercase tracking-widest hover:bg-gray-50 transition-colors ${sortBy === opt ? 'text-gold' : ''}`} data-id="element-970">
                        {opt}
                      </button>)}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 xl:grid-cols-3 gap-x-4 md:gap-x-8 gap-y-6 md:gap-y-8" data-id="element-971">
              {paginatedProducts.map((product, index) => <motion.div key={product.id} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: index % 6 * 0.05
            }} viewport={{
              once: true
            }} data-id="element-972">
                  <ProductCard product={{
                ...product,
                price: `${product.price}`,
                salePrice: product.salePrice ? `${product.salePrice}` : undefined
              }} data-id="element-973" />
                </motion.div>)}
            </div>

            {/* Pagination UI */}
            {totalPages > 1 && <div className="mt-16 flex justify-center items-center gap-2" data-id="element-974">
                <button onClick={() => handlePageChange(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="p-2 border border-gray-200 disabled:opacity-30 disabled:cursor-not-allowed hover:border-gold transition-colors" data-id="element-975">
                  <ChevronDown size={20} className="rotate-90" data-id="element-976" />
                </button>

                <div className="flex items-center gap-1" data-id="element-977">
                  {Array.from({
                length: totalPages
              }).map((_, i) => {
                const page = i + 1;
                // Simple logic to show current, first, last, and neighbors
                if (page === 1 || page === totalPages || page >= currentPage - 1 && page <= currentPage + 1) {
                  return <button key={page} onClick={() => handlePageChange(page)} className={`w-10 h-10 text-xs font-bold uppercase tracking-widest transition-all duration-300 ${currentPage === page ? 'bg-black text-white' : 'hover:bg-gray-50 border border-transparent hover:border-gray-200'}`} data-id="element-978">
                          {page}
                        </button>;
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="px-1 text-gray-400" data-id="element-979">
                          ...
                        </span>;
                }
                return null;
              })}
                </div>

                <button onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="p-2 border border-gray-200 disabled:opacity-30 disabled:cursor-not-allowed hover:border-gold transition-colors" data-id="element-980">
                  <ChevronDown size={20} className="-rotate-90" data-id="element-981" />
                </button>
              </div>}

            {filteredProducts.length === 0 && <div className="py-20 text-center" data-id="element-982">
                <p className="text-gray-400 font-serif text-xl" data-id="element-983">
                  No products found matching your criteria.
                </p>
                <button onClick={() => {
              setActiveCategory('All Products');
              setSearchQuery('');
              setSelectedFilters({
                collection: [],
                type: [],
                availability: []
              });
            }} className="mt-6 text-gold uppercase tracking-widest text-xs font-bold border-b border-gold pb-1" data-id="element-984">
                  Reset All Filters
                </button>
              </div>}
          </main>
        </div>
      </div>

      <SocialShopping data-id="element-985" />

      {/* Mobile Filters Overlay */}
      <AnimatePresence data-id="element-986">
        {showMobileFilters && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 bg-black/50 lg:hidden" data-id="element-987">
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} className="absolute right-0 top-0 bottom-0 w-4/5 bg-white p-8 overflow-y-auto" data-id="element-988">
              <div className="flex justify-between items-center mb-10" data-id="element-989">
                <h2 className="text-xl font-serif" data-id="element-990">Filters</h2>
                <button onClick={() => setShowMobileFilters(false)} data-id="element-991">
                  <X size={24} data-id="element-992" />
                </button>
              </div>

              <div className="space-y-10" data-id="element-993">
                {filterGroups.map(group => <div key={group.name} data-id="element-994">
                    <h3 className="text-xs uppercase tracking-widest font-bold mb-6 border-b border-gray-100 pb-2" data-id="element-995">
                      {group.name}
                    </h3>
                    <div className="flex flex-wrap gap-3" data-id="element-996">
                      {group.options.map(opt => <button key={opt} onClick={() => toggleFilter(group.key, opt)} className={`px-4 py-2 border text-xs uppercase tracking-widest transition-colors ${selectedFilters[group.key].includes(opt) ? 'bg-black text-white border-black' : 'border-gray-200 hover:border-gold hover:text-gold'}`} data-id="element-997">
                          {opt}
                        </button>)}
                    </div>
                  </div>)}
              </div>

              <button onClick={() => setShowMobileFilters(false)} className="w-full bg-black text-white py-4 mt-12 text-xs uppercase tracking-widest font-bold" data-id="element-998">
                Apply Filters
              </button>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}